export type Module = 'atypical' | 'shapeshifter' | 'practice' | 'assessor';

// --- ROLES & USERS ---
export enum Role {
  PLATFORM_ADMIN = 'PLATFORM_ADMIN',
  PARTNER_ADMIN = 'PARTNER_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
  TEACHER_USER = 'TEACHER_USER',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}



export enum InviteStatus {
  PENDING = 'PENDING',
  CLAIMED = 'CLAIMED',
  REVOKED = 'REVOKED',
}
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: Role;
  status: UserStatus;
  tenantId?: string | null;
  partnerId?: string | null;
}



export interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: InviteStatus;
  invitedBy: string;
  createdAt: any;
  displayName?: string;
  tenantId?: string | null;
  partnerId?: string | null;
}
// --- TENANTS & PARTNERS ---
export interface Partner {
  id: string;
  partnerName: string;
  partnerId: string; // unique, URL-friendly identifier e.g., "edtech-resellers-inc"
  contactEmail?: string;
  notes?: string;
  createdAt: any; // Firestore Timestamp
}

// FIX: Add missing TenantInfo type
export interface TenantInfo {
  id: string;
  name: string;
  address: string;
  initials: string;
}

export interface Tenant {
  id: string;
  tenantName: string;
  partnerId: string;
  createdAt: any; // Firestore Timestamp
  tenantLogoUrl: string;
  customPrompts?: Partial<Record<Module, string>>;
  entitlements: {
    aTypicalModule: boolean;
    shapeShifterModule: boolean;
    practiceMakesPermanentModule: boolean;
    theAssessorModule: boolean;
    reformatterModule: boolean;
  };
  limits: {
    maxPagesPerRun: number;
    maxFileSizeMb: number;
    maxRunsPerDay: number;
  };
  billingConfig: {
creditsPerRupee: number; // e.g., 0.5 means 1 credit = INR 2
    creditsPerPage: number; // e.g., 2
    freePoolCredits: number; // e.g., 10000
  };
  creditBalance: {
    freeRemaining: number;
    paidRemaining: number;
    freeCreditExpiryTimestamp: any; // Firestore Timestamp
  };
}

export interface AllowlistUser {
  email: string;
  addedBy: string; // UID of admin
  createdAt: any; // Firestore Timestamp
}

// --- CREDIT & RUNS ---
export enum LedgerEntryType {
  CONSUMPTION = 'CONSUMPTION',
  PAID_TOP_UP = 'PAID_TOP_UP',
  FREE_POOL_GRANT = 'FREE_POOL_GRANT',
  FREE_POOL_EXPIRED = 'FREE_POOL_EXPIRED',
  REFUND = 'REFUND',
}

export interface CreditLedgerEntry {
  id: string;
  timestamp: any; // Firestore Timestamp
  type: LedgerEntryType;
  amount: number; // Number of credits
  details: {
    runId?: string;
    userId?: string;
    memo?: string;
  };
  balanceAfter: {
    free: number;
    paid: number;
  };
}

export type RunStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export interface Run {
  id: string;
  tenantId: string;
  userId: string;
  module: Module;
  status: RunStatus;
  inputPages: number;
  creditsDeducted: number;
  balanceAfter?: { free: number; paid: number };
  createdAt: any; // Firestore Timestamp
  completedAt?: any; // Firestore Timestamp
  error?: string;
}

// --- MODULE SPECIFIC ---
export enum Grade {
  GRADE_1 = 'Grade 1',
  GRADE_2 = 'Grade 2',
  GRADE_3 = 'Grade 3',
  GRADE_4 = 'Grade 4',
  GRADE_5 = 'Grade 5',
  GRADE_6 = 'Grade 6',
  GRADE_7 = 'Grade 7',
  GRADE_8 = 'Grade 8',
  GRADE_9 = 'Grade 9',
  GRADE_10 = 'Grade 10',
  GRADE_11 = 'Grade 11',
  GRADE_12 = 'Grade 12',
}

export enum Subject {
  ENGLISH = 'English',
  MATHS = 'Maths',
  SCIENCE = 'Science',
  SOCIAL_STUDIES = 'Social Studies',
  OTHER = 'Other',
  ALL = 'All',
}

export enum Board {
    CBSE = 'CBSE',
    ICSE = 'ICSE',
    STATE_BOARD = 'State Board',
    CAMBRIDGE = 'Cambridge',
}

export enum CognitiveDemand {
  RECALL_HEAVY = 'Recall heavy',
  BALANCED = 'Balanced',
  REASONING_HEAVY = 'Reasoning heavy',
}

export enum GuidanceLevel {
  HIGH = 'High guidance',
  MEDIUM = 'Medium guidance',
  LOW = 'Low guidance',
}

export enum ContextLevel {
  TEXTBOOK_ONLY = 'Textbook only',
  MIXED = 'Mixed (textbook + simple real life)',
}

export enum AssessmentType {
  MID_TERM = 'Mid-Term Assessment',
  FINAL_EXAM = 'Final Examination',
  PA_1 = 'Periodic Assessment 1',
  PA_2 = 'Periodic Assessment 2',
  UNIT_TEST = 'Unit Test',
}

export enum QuestionType {
  MCQ = 'MCQ Only',
  SUBJECTIVE = 'Subjective Only',
  MIXED = 'Mixed (MCQ + Subjective)',
}

export enum WorksheetMode {
  GENERIC = 'generic',
  DIAGNOSTIC_BASED = 'diagnostic_based',
}

export enum DensityLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type ArtifactType = 'questionPaper' | 'answerKey' | 'markingRubric' | 'assessmentBlueprint';

export interface LearningOutcomeItem {
  lo_key: string;
  lo_text: string;
  strand?: string;
  keywords?: string[];
}

// FIX: Add missing DiagnosticResult type
export interface DiagnosticResult {
  title: string;
  summary: string;
  strengths: string[];
  areasForImprovement: string[];
  recommendations: string[];
}

// FIX: Add missing OutcomeIntegrityMapEntry type
export interface OutcomeIntegrityMapEntry {
  learningOutcome: string;
  assessmentMethod: string;
  alignment: 'High' | 'Medium' | 'Low';
}

// FIX: Add missing LessonArchitectureSection type
export interface LessonArchitectureSection {
  title: string;
  content: string[];
}
export interface LoRegistryItem {
  id: string;              // Firestore doc id for lo_items/{id}
  board: string;           // must match boardAlignment boardId exactly (case-sensitive in server.js)
  subject: string;         // compared case-insensitively in server.js
  grade?: string | null;   // used indirectly (grade_norm, grade_label not required but allowed)
  topic?: string | null;
  outcome: string;         // primary outcome text
  doc_id: string;          // registry batch id (your generatedDocId)
  version: string;         // ISO string
  title?: string | null;
  scope?: string | null;

  // Optional compatibility fields (server.js can read from many)
  lo_text?: string | null; // if you want redundancy, set = outcome
}

export interface LessonPlanOutput {
  // FIX: Use specific types instead of any[]
  outcomeIntegrityMap: OutcomeIntegrityMapEntry[];
  lessonArchitecture: LessonArchitectureSection[];
}
export interface PendingInvite {
  inviteId: string;
  email?: string;
  role: Role;
  tenantId?: string | null;
  tenantName?: string | null;
  partnerId?: string | null;
  displayName?: string | null;
  createdAt?: any;
}
